/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useSyncExternalStore } from 'react';
import defaultTheme from 'tailwindcss/defaultTheme';

/** Tailwind `md` breakpoint — below it the cancel/return flows present as a bottom-sheet Drawer, at/above it a centered Dialog. */
const DESKTOP_MEDIA_QUERY = `(min-width: ${defaultTheme.screens.md})`;

function subscribeToDesktopQuery(callback: () => void): () => void {
    const mql = globalThis.matchMedia?.(DESKTOP_MEDIA_QUERY);
    mql?.addEventListener('change', callback);
    return () => mql?.removeEventListener('change', callback);
}

/** Client snapshot: true at/above `md`, false below. Defaults to desktop when `matchMedia` is unavailable. */
function getIsDesktopSnapshot(): boolean {
    return globalThis.matchMedia?.(DESKTOP_MEDIA_QUERY)?.matches ?? true;
}

/** Server snapshot: assume desktop (Dialog). The overlay is closed at first paint and only opens post-hydration, so there is no shell flash. */
function getIsDesktopServerSnapshot(): boolean {
    return true;
}

/**
 * Present an overlay as a centered `Dialog` at Tailwind's `md` breakpoint and up, and a
 * full-height bottom-sheet `Drawer` below it. Hydration-safe via a shared `matchMedia`
 * subscription with a desktop server snapshot — mirrors the account Return dialog's
 * `useIsDesktop` so the guest cancel/return flows behave identically on mobile.
 */
export function useIsDesktop(): boolean {
    return useSyncExternalStore(subscribeToDesktopQuery, getIsDesktopSnapshot, getIsDesktopServerSnapshot);
}
